import { useNavigate } from 'react-router-dom';
import React, { forwardRef, useImperativeHandle } from 'react';

import { useFormikContext } from 'formik';

import { updateAddress as updateAddressAPI } from 'api/Address';

import { iaRa, optionify } from 'lib/helpers/utility';
import { useStates } from 'lib/hooks/queries/states/useStates';

import Input from 'components/shared/Input/Input';
import Select from 'components/shared/Select/Select';
import Divider from 'components/shared/Divider/Divider';
import { showAlert } from 'components/shared/Alert/Alert';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import SectionList from 'components/practice/UserPreferences/components/SectionList';

import AdditionalLocations from './AdditionalLocations';

const PracticeLocation = forwardRef((_, ref) => {
  const { data } = useStates();
  const states = iaRa(data?.states);
  const navigate = useNavigate();
  const { setFieldValue, values, errors } = useFormikContext();

  const onAddressChange = (event) => {
    let field;
    let value;
    if (event?.name == 'state') {
      field = event?.name;
      value = event?.value || null;
    } else {
      field = event.target?.name;
      value = event.target?.value;
    }
    setFieldValue('address', {
      ...values?.address,
      [field]: value,
      full_street_address: `${values?.address?.address_ln_1}, ${values?.address?.address_ln_2}, ${values?.address?.city}, ${values?.address?.state}, ${values?.address.country}, ${values?.address.zip}`
    });
  };

  const findStateName = (value) => {
    const state = states?.find((state) => {
      if (state?.id == value?.state) {
        return state;
      }
    });

    return state && state?.name;
  };

  useImperativeHandle(ref, () => ({
    updateAddress
  }));

  const updateAddress = async () => {
    if (values?.address) {
      const res = await updateAddressAPI(navigate, values?.address);
      if (res.code === 0) {
        setFieldValue('address', values?.address);
      } else {
        showAlert({
          message: "Couldn't update the address. Please try again.",
          color: 'danger'
        });
      }
    }
  };

  return (
    states && (
      <>
        <SectionList
          title="Practice location"
          description="Enter your official practice address for accurate documentation and communications.">
          <Input
            label="Address label"
            type="text"
            placeholder="Primary location"
            name="addressLabel"
            value={values?.addressLabel}
            onChange={(e) => setFieldValue('addressLabel', e.target.value)}
            error={errors?.addressLabel}
            maxLength={255}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Address 1"
              type="text"
              placeholder="Address"
              name="address_ln_1"
              value={values?.address?.address_ln_1}
              onChange={(e) => onAddressChange(e)}
              error={errors?.address?.address_ln_1}
            />
            <Input
              label="Address 2"
              type="text"
              placeholder="Address 2"
              name="address_ln_2"
              value={values?.address?.address_ln_2}
              onChange={(e) => onAddressChange(e)}
            />
            <Input
              label="City"
              type="text"
              placeholder="City"
              name="city"
              value={values?.address?.city}
              onChange={(e) => onAddressChange(e)}
              error={errors?.address?.city}
            />
            <Select
              label="State"
              inputId="state-select"
              placeholder="Type and select"
              name="state"
              value={{ label: findStateName(values?.address) }}
              onChange={(e) => onAddressChange({ ...e, name: 'state' })}
              options={optionify(states, 'name', 'id')}
              error={errors?.address?.state}
            />
            <Input
              label="Zip code"
              type="text"
              placeholder="Zip"
              name="zip"
              value={values?.address?.zip}
              onChange={(e) => onAddressChange(e)}
              error={errors?.address?.zip}
            />
          </div>
        </SectionList>
        <Divider />
        <AdditionalLocations />
      </>
    )
  );
});

export default withErrorBoundary(PracticeLocation);
